// BracketView — single-elimination draw rendered as flex columns.

import { Bracket, BracketMatch } from '@/config/tournamentConfig'
import { formatSets } from '@/game/systems/MatchSimulator'
import { ClubCrest } from './broadcast'
import { Check } from 'lucide-react'

function roundLabel(round: number, totalRounds: number): string {
  const remaining = totalRounds - round
  if (remaining === 1) return 'Final'
  if (remaining === 2) return 'Semifinales'
  if (remaining === 3) return 'Cuartos'
  return `Ronda ${round + 1}`
}

function TeamRow({ match, side }: { match: BracketMatch; side: 0 | 1 }) {
  const team = side === 0 ? match.teamA : match.teamB
  const isWinner = match.result?.winner === side
  const isLoser = match.result !== null && !isWinner

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.5rem',
      fontSize: '0.7rem',
      padding: '0.15rem 0.35rem',
      borderRadius: '3px',
      background: team?.isClubTeam ? 'var(--gold-fill)' : 'transparent',
      color: team?.isClubTeam
        ? 'var(--gold)'
        : isLoser ? 'var(--text-secondary)' : 'var(--text-strong)',
      fontWeight: isWinner ? 600 : 400,
      opacity: isLoser ? 0.6 : 1,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', overflow: 'hidden' }}>
        {team && <ClubCrest name={team.name} size={14} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {team?.name ?? '—'}
        </span>
      </span>
      {isWinner && <Check size={12} style={{ flexShrink: 0 }} />}
    </div>
  )
}

export function BracketView({ bracket }: { bracket: Bracket }) {
  const rounds = Array.from({ length: bracket.totalRounds }, (_, r) =>
    bracket.matches.filter(m => m.round === r)
  )

  return (
    <div style={{ overflowX: 'auto', paddingBottom: '0.5rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', minWidth: 'min-content' }}>
        {rounds.map((matches, r) => (
          <div key={r} style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            gap: '0.5rem',
            minWidth: '130px',
          }}>
            <div className="section-label" style={{ textAlign: 'center' }}>
              {roundLabel(r, bracket.totalRounds)}
            </div>
            {matches.map(match => (
              <div key={match.id} style={{
                background: 'var(--fill-softer)',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                padding: '0.25rem',
              }}>
                <TeamRow match={match} side={0} />
                <TeamRow match={match} side={1} />
                {match.result && (
                  <div className="meta-text" style={{
                    fontSize: '0.62rem',
                    padding: '0.1rem 0.35rem',
                    borderTop: '1px solid var(--border-default)',
                    marginTop: '0.15rem',
                  }}>
                    {formatSets(match.result)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
